import { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  Button,
  InlineNotification,
  Loading,
  Tile,
  Layer,
} from '@carbon/react';
import { Edit, TrashCan } from '@carbon/react/icons';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from 'components/page-header';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { VerificationControls } from 'components/verification-controls';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import { formatResource } from 'utils/misc';
import { pageTitle } from 'utils/page-title';
import { AfeDetailPanel } from './afe-detail-panel';
import { AfeScenarioPanel } from './afe-scenario-panel';
import { CostDetailsTable } from './cost-details-table';
import { SupportingFilesPanel } from './supporting-files-panel';
import { formatAfeLabel } from './utils';
import { SourceDocumentBanner } from '../source-document-banner';
import { useAfeDetailAtom } from './atoms';
import { deleteAfeLoadableAtom } from '../atoms';
import styles from './page.module.scss';

const AfeCatalogDetailPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const history = useHistory();

  const resource = useAtomValue(useAfeDetailAtom(id));
  const { loading, error, data } = formatResource(resource);
  const detail = data?.data;

  const title = formatAfeLabel(detail?.id ?? id);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  const [deleteLoadable, deleteAfe] = useAtom(deleteAfeLoadableAtom);
  const isDeleting = deleteLoadable.state === 'loading';

  const clearDeleteError = () => setDeleteError(undefined);

  const handleConfirmDelete = async () => {
    clearDeleteError();
    try {
      await deleteAfe(Number(id));
      setIsConfirmOpen(false);
      history.push('/afe_catalog');
    } catch {
      // Per-status failure copy is pending Open Question #4. Surface a
      // generic message so the user gets a signal until UX specs the
      // exact 404 / 422 / network treatment. DestroyConfirmation appends
      // its own "Please try again!" subtitle, so keep this title short.
      setDeleteError('Unable to delete AFE.');
    }
  };

  const handleOpenConfirm = () => {
    clearDeleteError();
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    // Block close (Cancel, X icon, Escape, click-outside) while the delete
    // is in flight — committed delete should not be backgrounded.
    if (isDeleting) return;
    clearDeleteError();
    setIsConfirmOpen(false);
  };

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Unable to load AFE details"
        subtitle={error}
      />
    );
  }

  if (loading && !detail) {
    return (
      <>
        <div className={styles.breadcrumbsOnlyHeader}>
          <PageHeader
            breadcrumbs={[
              ['/afe_catalog', 'AFE Catalog'],
              [location.pathname, title],
            ]}
            title={null}
            actions={null}
          />
        </div>
        <div className={styles.container}>
          <Layer>
            <Tile className={styles.loadingTile}>
              <Loading
                withOverlay={false}
                small
                description="Loading AFE details..."
              />
            </Tile>
          </Layer>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <InlineNotification
        kind="warning"
        lowContrast
        title="AFE details not available"
        subtitle="No detail payload was returned for this AFE."
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle(title)}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[
          ['/afe_catalog', 'AFE Catalog'],
          [location.pathname, title],
        ]}
        title={
          <div className={styles.titleRow}>
            <h1>{title}</h1>
            {detail.verifications?.afe && (
              <VerificationControls
                path={['afe']}
                displayName="AFE"
                skipModal
                hideComments
                eventsHref={detail.verifications.afe.href.events}
                commentsHref={detail.verifications.afe.href.comments}
                initialStatus={detail.verifications.afe.status}
                initialCommentsCount={detail.verifications.afe.commentsCount}
              />
            )}
          </div>
        }
        actions={
          <div className={styles.headerActions}>
            <ActivityFeedPanelOpenButton />
            <Button
              kind="danger--tertiary"
              size="md"
              renderIcon={TrashCan}
              onClick={handleOpenConfirm}
            >
              Delete
            </Button>
            <Button
              kind="primary"
              size="md"
              onClick={() => {
                // Auto-connected records (those with a source document) edit
                // through the structuring workflow, which lives on the source
                // document's overview page; manually-entered records stay
                // editable in place (P6). For the auto-connected case, deep-link
                // straight to this AFE's Structure Costs (the catalog `id` IS the
                // afe_header_id the Cost section keys on) via location state.
                if (detail.sourceDocument) {
                  history.push({
                    pathname: `/afe_documents/${detail.sourceDocument.id}`,
                    state: { afeCostWellId: Number(detail.id) },
                  });
                } else {
                  history.push(`/afe_catalog/${id}/edit`);
                }
              }}
              renderIcon={Edit}
            >
              Edit
            </Button>
          </div>
        }
      />

      <div className={styles.container}>
        {detail.sourceDocument && (
          <div className={styles.sectionPanel}>
            <SourceDocumentBanner sourceDocument={detail.sourceDocument} />
          </div>
        )}
        <div className={styles.sectionPanel}>
          <AfeDetailPanel detail={detail} />
        </div>
        {detail.afeType === 'dc_and_e' && (
          <div className={styles.sectionPanel}>
            <AfeScenarioPanel detail={detail} />
          </div>
        )}
        <div className={styles.sectionPanel}>
          <CostDetailsTable
            lineItems={detail.costSummary.lineItems}
            totals={detail.costSummary.totals}
          />
        </div>
        <div className={styles.sectionPanel}>
          <SupportingFilesPanel
            files={detail.supportingFiles}
            afeId={detail.id}
          />
        </div>
      </div>

      <DestroyConfirmation
        open={isConfirmOpen}
        onRequestClose={handleCloseConfirm}
        onRequestSubmit={handleConfirmDelete}
        modalHeading="Delete AFE Record"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        loadingStatus={isDeleting ? 'active' : 'inactive'}
        preventCloseOnClickOutside={isDeleting}
        error={deleteError}
      >
        Are you sure you want to delete <strong>{title}</strong>? This action
        cannot be undone.
      </DestroyConfirmation>

      <ActivityFeedPanel
        modelType="AfeHeader"
        modelId={Number(detail.id)}
        title="Activity"
        subtitle={title}
        commentsHref=""
      />
    </>
  );
};

export { AfeCatalogDetailPage };
